/**
 * News & events grid with client-side filtering and search.
 *
 * Markup contract:
 * .§[data-endpoint][data-stage-language][data-type][data-limit]
 *   .news-events-grid__controls
 *     .news-events-grid__filters
 *       button.news-events-grid__filter[data-filter-value]
 *     label.news-events-grid__search
 *       input.news-events-grid__search-input
 *   script.news-events-grid__i18n[type="application/json"]
 *   p.news-events-grid__status[aria-live="polite"]
 *   div.news-events-grid__results
 *   div.news-events-grid__actions
 *     button.news-events-grid__load-more
 *
 * @module news-events-grid
 */

import { pick, escapeHtml, formatMessage, getStageLanguage, getImageUrl, resolveItemUrl, resolveReadMoreText } from './grid-utils.js';

const DEFAULT_ENDPOINT = '/stage-ajax/grid-search';
const DEFAULT_TYPE = 'some';
const DEFAULT_LIMIT = '12';
const DEFAULT_FETCH_LIMIT = '200';

/**
 * @typedef {Object} NewsEventsTranslations
 * @property {string} loading
 * @property {string} loaded
 * @property {string} empty
 * @property {string} error
 * @property {string} loadMore
 * @property {string} readMore
 * @property {string} searchPlaceholder
 * @property {string} featured
 * @property {string} all
 */

/**
 * @typedef {Object} NormalizedNewsItem
 * @property {string} title
 * @property {string} caption
 * @property {string} url
 * @property {(Object|string)} imageUrl - Either { jpg, webp } object or string URL.
 * @property {string} badge
 * @property {string} readMoreText
 * @property {string} itemType
 * @property {string} date
 * @property {boolean} featured
 */

/** @type {NewsEventsTranslations} */
const DEFAULT_TRANSLATIONS = {
  loading: 'Loading results...',
  loaded: '{shown} of {total} results loaded.',
  empty: 'No results found.',
  error: 'Unable to load results right now.',
  loadMore: 'Load more',
  readMore: 'Read more',
  searchPlaceholder: 'Search',
  featured: 'Featured',
  all: 'All'
};

/**
 * Reads localized UI strings from the component JSON script tag.
 *
 * @param {HTMLElement} root - Component root element.
 * @returns {NewsEventsTranslations}
 */
function getTranslations(root) {
  const script = root.querySelector('.news-events-grid__i18n');

  if (!(script instanceof HTMLScriptElement)) {
    return DEFAULT_TRANSLATIONS;
  }

  try {
    const parsed = JSON.parse(script.textContent || '{}');

    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return DEFAULT_TRANSLATIONS;
    }

    return {
      ...DEFAULT_TRANSLATIONS,
      ...parsed
    };
  } catch {
    return DEFAULT_TRANSLATIONS;
  }
}

/**
 * Normalizes one API item for rendering.
 *
 * @param {Record<string, any>} item - Raw API item.
 * @param {NewsEventsTranslations} translations - Localized UI strings.
 * @returns {NormalizedNewsItem}
 */
function normalizeItem(item, translations) {
  const itemType = String(pick(item, ['type', 'category', 'label']) || '').trim();
  const featuredValue = pick(item, ['featured', 'isFeatured', 'is_featured']);
  const featured = featuredValue === 1 || featuredValue === '1';
  const badge = featured ? translations.featured : translations[item.type] || '';
  const readMoreText = resolveReadMoreText(item, translations);

  return {
    title: pick(item, ['title', 'headline', 'heading', 'name']) || '',
    caption: pick(item, ['caption', 'description', 'excerpt', 'summary', 'ingress']) || '',
    url: resolveItemUrl(item),
    imageUrl: getImageUrl(item),
    badge,
    readMoreText,
    itemType,
    date: pick(item, ['date', 'publish_date', 'published_at', 'datetime']) || '',
    featured
  };
}

/**
 * Extracts items from a response payload.
 *
 * @param {Record<string, any>} payload - Parsed response payload.
 * @returns {Record<string, any>[]}
 */
function extractItems(payload) {
  const directItems = pick(payload, ['items', 'results', 'news', 'data']);
  if (Array.isArray(directItems)) return directItems;

  if (payload?.data && typeof payload.data === 'object') {
    const nestedItems = pick(payload.data, ['items', 'results', 'news', 'data']);
    if (Array.isArray(nestedItems)) return nestedItems;
  }

  return [];
}

/**
 * Extracts total count from payload.
 *
 * @param {Record<string, any>} payload - Parsed response payload.
 * @returns {number}
 */
function extractTotal(payload) {
  const total = pick(payload, ['total', 'count']);
  return Number.isFinite(Number(total)) ? Number(total) : extractItems(payload).length;
}

/**
 * Builds the initial request URL.
 *
 * @param {HTMLElement} root - Component root element.
 * @param {number} limit - Fetch limit.
 * @returns {URL}
 */
function buildRequestUrl(root, limit) {
  const endpoint = root.dataset.endpoint || DEFAULT_ENDPOINT;
  const requestUrl = new URL(endpoint, window.location.origin);

  requestUrl.searchParams.set('stage-language', getStageLanguage(root));
  requestUrl.searchParams.set('type', root.dataset.type || DEFAULT_TYPE);
  requestUrl.searchParams.set('limit', String(limit));

  const gridSearch = root.dataset.gridSearch?.trim();
  if (gridSearch) {
    requestUrl.searchParams.set('grid_search', gridSearch);
  }

  return requestUrl;
}

/**
 * Creates responsive picture markup with webp support.
 *
 * @param {Object} imageData - Image URLs object.
 * @param {string} imageData.jpg - JPEG image URL.
 * @param {(string|null)} imageData.webp - WebP image URL, or null if unavailable.
 * @returns {string}
 */
function renderPictureMarkup(imageData) {
  if (!imageData?.jpg) {
    return '';
  }

  const webpSource = imageData.webp
    ? `<source srcset="${escapeHtml(imageData.webp)}" type="image/webp">`
    : '';

  return `<picture>
    ${webpSource}
    <img src="${escapeHtml(imageData.jpg)}" alt="">
  </picture>`;
}

/**
 * Renders one card HTML string.
 *
 * @param {NormalizedNewsItem} item - Normalized card item.
 * @returns {string}
 */
function renderCard(item) {
  const themeClass = item.featured ? ' news-events-grid__card--featured' : '';
  let imageMarkup;

  if (item.imageUrl) {
    if (typeof item.imageUrl === 'object' && item.imageUrl.jpg) {
      imageMarkup = `<div class="news-events-grid__card-media">${renderPictureMarkup(item.imageUrl)}</div>`;
    } else if (typeof item.imageUrl === 'string') {
      imageMarkup = `<div class="news-events-grid__card-media"><img src="${escapeHtml(item.imageUrl)}" alt=""></div>`;
    } else {
      imageMarkup = '<div class="news-events-grid__card-media news-events-grid__card-media--empty" aria-hidden="true"></div>';
    }
  } else {
    imageMarkup = '<div class="news-events-grid__card-media news-events-grid__card-media--empty" aria-hidden="true"></div>';
  }

  const displayItemType = item.itemType
    ? item.itemType.charAt(0).toUpperCase() + item.itemType.slice(1)
    : '';
  const metaParts = [displayItemType, item.date].filter(Boolean);
  const metaMarkup = metaParts.length
    ? `<ul class="news-events-grid__meta">${metaParts.map((value) => `<li>${escapeHtml(value)}</li>`).join('')}</ul>`
    : '';

  return `
    <article class="news-events-grid__card${themeClass}">
      <a class="news-events-grid__card-link" href="${escapeHtml(item.url)}">
        <div class="news-events-grid__card-shell">
          ${imageMarkup}
          ${item.badge ? `<span class="news-events-grid__badge">${escapeHtml(item.badge)}</span>` : ''}
          <div class="news-events-grid__card-content">
            <h3 class="news-events-grid__card-title">${escapeHtml(item.title)}</h3>
            ${item.caption ? `<p class="news-events-grid__card-text">${escapeHtml(item.caption)}</p>` : ''}
            <span class="btn btn-white news-events-grid__card-cta" aria-hidden="true">
              ${escapeHtml(item.readMoreText)}
              <svg viewBox="0 0 16 16" focusable="false" height="12" width="12" aria-hidden="true">
                <use href="#chevron-right"></use>
              </svg>
            </span>
          </div>
        </div>
      </a>
    </article>
  `;
}

/**
 * Updates the status text.
 *
 * @param {HTMLElement|null} status - Live region.
 * @param {string} message - Status text.
 * @returns {void}
 */
function updateStatus(status, message) {
  if (status) {
    status.textContent = message;
  }
}

/**
 * Returns current active filter value.
 *
 * @param {HTMLElement[]} buttons - Filter buttons.
 * @returns {string}
 */
function getActiveFilter(buttons) {
  const activeButton = buttons.find((button) => button.getAttribute('aria-pressed') === 'true');
  return activeButton?.dataset.filterValue || 'all';
}

/**
 * Checks if item matches current UI filters.
 *
 * @param {NormalizedNewsItem} item - Normalized card item.
 * @param {string} activeFilter - Active pill filter.
 * @param {string} searchTerm - Current search term.
 * @returns {boolean}
 */
function matchesNewsFilters(item, activeFilter, searchTerm) {
  const itemType = item.itemType.toLowerCase();
  const normalizedSearch = searchTerm.trim().toLowerCase();

  const matchesType = activeFilter === 'all'
    || itemType === activeFilter
    || itemType.includes(activeFilter);

  if (!matchesType) return false;

  if (!normalizedSearch) return true;

  const haystack = [
    item.title,
    item.caption,
    item.itemType,
    item.date
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return haystack.includes(normalizedSearch);
}

/**
 * Initialises one news-events-grid root.
 *
 * @param {HTMLElement} root - Component root.
 * @returns {void}
 */
function initNewsEventsGridSection(root) {
  if (root.dataset.newsEventsGridInitialized === 'true') return;

  const results = root.querySelector('.news-events-grid__results');
  const status = root.querySelector('.news-events-grid__status');
  const loadMoreButton = root.querySelector('.news-events-grid__load-more');
  const filterButtons = Array.from(root.querySelectorAll('.news-events-grid__filter'));
  const searchInput = root.querySelector('.news-events-grid__search-input');
  const translations = getTranslations(root);

  if (!(results instanceof HTMLElement)) return;

  root.dataset.newsEventsGridInitialized = 'true';

  /** @type {AbortController | null} */
  let activeRequest = null;
  /** @type {NormalizedNewsItem[]} */
  let allItems = [];
  const baseLimit = Number.parseInt(root.dataset.limit || DEFAULT_LIMIT, 10) || Number.parseInt(DEFAULT_LIMIT, 10);
  const fetchLimit = Number.parseInt(root.dataset.fetchLimit || DEFAULT_FETCH_LIMIT, 10) || Number.parseInt(DEFAULT_FETCH_LIMIT, 10);
  let currentLimit = baseLimit;
  let currentCount = 0;
  let totalCount = 0;

  if (searchInput instanceof HTMLInputElement) {
    searchInput.placeholder = translations.searchPlaceholder;
  }

  /**
   * Updates load more button state.
   *
   * @returns {void}
   */
  function updateLoadMoreState() {
    if (!(loadMoreButton instanceof HTMLButtonElement)) return;

    const hasMore = currentCount < totalCount;
    loadMoreButton.hidden = !hasMore;
    loadMoreButton.disabled = root.dataset.loading === 'true';
    loadMoreButton.textContent = translations.loadMore;
  }

  /**
   * Renders current visible results.
   *
   * @param {number|null} focusIndex - Index to focus after render.
   * @returns {void}
   */
  function renderCurrentResults(focusIndex = null) {
    const activeFilter = getActiveFilter(filterButtons);
    const searchTerm = searchInput instanceof HTMLInputElement ? searchInput.value : '';

    const filteredItems = allItems
      .filter((item) => matchesNewsFilters(item, activeFilter, searchTerm))
      .sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));
    const visibleItems = filteredItems.slice(0, currentLimit);

    results.innerHTML = visibleItems.length
      ? visibleItems.map(renderCard).join('')
      : `<p class="news-events-grid__empty">${escapeHtml(translations.empty)}</p>`;

    currentCount = visibleItems.length;
    totalCount = filteredItems.length;

    updateStatus(status, formatMessage(translations.loaded, {
      shown: currentCount,
      total: totalCount
    }));
    updateLoadMoreState();

    if (Number.isInteger(focusIndex) && focusIndex >= 0) {
      const cardLinks = results.querySelectorAll('.news-events-grid__card-link');
      const targetLink = cardLinks[focusIndex];

      if (targetLink instanceof HTMLElement) {
        requestAnimationFrame(() => {
          targetLink.focus();
        });
      }
    }
  }

  /**
   * Fetches all items once.
   *
   * @returns {Promise<void>}
   */
  async function fetchAllResults() {
    activeRequest?.abort();
    activeRequest = new AbortController();

    const requestUrl = buildRequestUrl(root, fetchLimit);
    root.dataset.loading = 'true';
    updateStatus(status, translations.loading);
    updateLoadMoreState();

    try {
      const response = await fetch(requestUrl.toString(), {
        method: 'GET',
        credentials: 'include',
        headers: {
          Accept: 'application/json'
        },
        signal: activeRequest.signal
      });

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }

      const payload = await response.json();
      console.debug('[news-events-grid] request url:', requestUrl.toString());
      console.debug('[news-events-grid] raw payload:', payload);
      console.debug('[news-events-grid] extracted items:', extractItems(payload));

      allItems = extractItems(payload).map((item) => normalizeItem(item, translations));
      totalCount = extractTotal(payload);

      renderCurrentResults();
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;

      allItems = [];
      currentCount = 0;
      totalCount = 0;
      results.innerHTML = `<p class="news-events-grid__empty">${escapeHtml(translations.error)}</p>`;
      updateStatus(status, translations.error);
    } finally {
      root.dataset.loading = 'false';
      updateLoadMoreState();
    }
  }

  filterButtons.forEach((button) => {
    button.addEventListener('click', () => {
      filterButtons.forEach((otherButton) => {
        otherButton.setAttribute('aria-pressed', String(otherButton === button));
      });

      currentLimit = baseLimit;
      renderCurrentResults();
    });
  });

  if (searchInput instanceof HTMLInputElement) {
    searchInput.addEventListener('input', () => {
      currentLimit = baseLimit;
      renderCurrentResults();
    });
  }

  if (loadMoreButton instanceof HTMLButtonElement) {
    loadMoreButton.addEventListener('click', () => {
      const firstNewItemIndex = currentCount;
      currentLimit += baseLimit;
      renderCurrentResults(firstNewItemIndex);
    });
  }

  fetchAllResults();
}

/**
 * Initialises all news-events-grid sections.
 *
 * @returns {void}
 */
export function initNewsEventsGrid() {
  const sections = document.querySelectorAll('.news-events-grid');
  sections.forEach((section) => {
    if (section instanceof HTMLElement) {
      initNewsEventsGridSection(section);
    }
  });
}
